'use client'
import { Button } from '@/components/ui/button'
import Link from 'next/link'
import { Flame, X, ArrowRight } from 'lucide-react'
import { useTranslation } from '@/lib/i18n'

export interface StreakAtRiskBannerProps {
  streakCurrent: number
  daysUntilDecay: number   // days remaining in the 7-day grace; 0 means "today is the last day"
  /** REQUIRED — it used to default to '/pt-play', a route that has never
   *  existed. See NextRoundCTAProps.playHref for the full note. */
  playHref: string
  onDismiss?: () => void
}

export function StreakAtRiskBanner({
  streakCurrent,
  daysUntilDecay,
  playHref,
  onDismiss,
}: StreakAtRiskBannerProps) {
  const { t } = useTranslation()
  const urgency = daysUntilDecay <= 0 ? 'critical' : daysUntilDecay === 1 ? 'warning' : 'notice'
  const colorClass = urgency === 'critical'
    ? 'border-red-500/50 bg-red-500/10 text-red-700 dark:text-red-200'
    : 'border-orange-500/40 bg-orange-500/10 text-orange-700 dark:text-orange-200'

  return (
    <div className={`relative flex items-center gap-3 rounded-xl border px-4 py-3 ${colorClass}`}>
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-orange-500/20 border border-orange-500/40">
        <Flame className="h-5 w-5 text-orange-500" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold">
          {urgency === 'critical'
            ? t('learner.cards.streakRisk.aboutToBreak', { count: streakCurrent })
            : t('learner.cards.streakRisk.atRisk', { count: streakCurrent })}
        </div>
        <div className="text-xs opacity-80">
          {daysUntilDecay <= 0
            ? t('learner.cards.streakRisk.lastDay')
            : t('learner.cards.streakRisk.playWithin', { count: daysUntilDecay })}
        </div>
      </div>
      <Button asChild size="sm" variant="default">
        <Link href={playHref}>
          {t('learner.cards.streakRisk.save')} <ArrowRight className="h-3 w-3 ms-1 rtl-flip" />
        </Link>
      </Button>
      {onDismiss && (
        <button
          aria-label={t('learner.cards.common.dismiss')}
          onClick={onDismiss}
          className="absolute top-1.5 end-1.5 h-6 w-6 rounded-full hover:bg-foreground/10 flex items-center justify-center text-muted-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  )
}
